// components/icons/ServiceIcons.tsx
import React, { useId, memo } from "react";

/** Shared props across all icons */
export type IconProps = {
  /** Pixel size or CSS size; applied to both width & height. Default: 32 */
  size?: number | string;
  /** Optional title for accessibility. If omitted, icon is marked decorative. */
  title?: string;
  /** Extra class names (e.g., "h-6 w-6") */
  className?: string;
  /** Control stroke width scaling for crispness at 24–32px. */
  strokeWidth?: number;
  /** Optional override for gradient direction */
  gradient?: {
    x1?: string; y1?: string; x2?: string; y2?: string;
    stops?: Array<{ offset: string; color: string }>;
  };
};

/** Utility: sanitize useId() for use inside url(#id) */
function safeId(raw: string) {
  return `grad-${raw.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

/** Base SVG wrapper for consistent a11y & sizing */
function IconBase({
  size = 32,
  title,
  className,
  children,
  viewBox = "0 0 32 32",
}: React.PropsWithChildren<{
  size?: number | string;
  title?: string;
  className?: string;
  viewBox?: string;
}>) {
  const titleId = useId();
  const labelled = title
    ? { "aria-labelledby": titleId, role: "img" as const }
    : { "aria-hidden": true as const, role: "img" as const };
  return (
    <svg
      viewBox={viewBox}
      width={size}
      height={size}
      focusable={false}
      {...labelled}
      className={className}
    >
      {title ? <title id={titleId}>{title}</title> : null}
      {children}
    </svg>
  );
}

/** Gradient defs with per-instance unique id (prevents collisions) */
function HoloGradientDefs({
  id,
  gradient,
  fallbackStops,
}: {
  id: string;
  gradient?: IconProps["gradient"];
  fallbackStops: Array<{ offset: string; color: string }>;
}) {
  const {
    x1 = "0%", y1 = "0%", x2 = "100%", y2 = "100%",
    stops = fallbackStops,
  } = gradient || {};
  return (
    <defs>
      <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
        {stops.map((s, i) => (
          <stop key={i} offset={s.offset} stopColor={s.color} />
        ))}
      </linearGradient>
    </defs>
  );
}

/* ========================== Icons ========================== */

/** ---------- GoalBuddy (target + sparkle) ---------- */
export const GoalBuddyIcon: React.FC<IconProps> = memo(function GoalBuddyIcon({
  size,
  title,
  className,
  strokeWidth = 1.6,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(200,95%,70%)" },
          { offset: "60%", color: "hsl(300,95%,70%)" },
          { offset: "100%", color: "hsl(340,95%,70%)" },
        ]}
      />
      {/* Target rings */}
      <circle cx="16" cy="16" r="10" fill="none" stroke={`url(#${gradId})`} strokeWidth={2} />
      <circle cx="16" cy="16" r="6"  fill="none" stroke={`url(#${gradId})`} strokeWidth={strokeWidth} />
      {/* Bullseye */}
      <circle cx="16" cy="16" r="2.4" fill={`url(#${gradId})`} />
      {/* Spark (north-east) */}
      <g fill="none" stroke={`url(#${gradId})`} strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 6.5v5" strokeWidth={1.4} />
        <path d="M20.5 9h5" strokeWidth={1.4} />
        <path d="M21.6 7.4l3 3M24.6 7.4l-3 3" strokeWidth={1.2} />
      </g>
    </IconBase>
  );
});

/** ---------- OmniCast (broadcast arcs + node) ---------- */
export const OmniCastIcon: React.FC<IconProps> = memo(function OmniCastIcon({
  size,
  title,
  className,
  strokeWidth = 2.5,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(190,95%,70%)" },
          { offset: "50%", color: "hsl(280,95%,70%)" },
          { offset: "100%", color: "hsl(340,95%,70%)" },
        ]}
      />
      <g fill="none" stroke={`url(#${gradId})`} strokeLinecap="round">
        <path strokeWidth={strokeWidth} d="M5 21c3-5 8-8 13-8" />
        <path strokeWidth={strokeWidth} d="M5 25c5-8 13-12 21-12" />
      </g>
      <circle cx="8" cy="26" r="2.6" fill={`url(#${gradId})`} />
    </IconBase>
  );
});

/** ---------- OmniCine (play + film frame) ---------- */
export const OmniCineIcon: React.FC<IconProps> = memo(function OmniCineIcon({
  size,
  title,
  className,
  strokeWidth = 2,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(200,95%,70%)" },
          { offset: "60%", color: "hsl(320,95%,70%)" },
          { offset: "100%", color: "hsl(40,95%,70%)" },
        ]}
      />
      <rect
        x="6" y="7" width="20" height="18" rx="3"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
      />
      <polygon points="13,11 23,16 13,21" fill={`url(#${gradId})`} />
      {/* film sprockets */}
      <g fill="none" stroke={`url(#${gradId})`} strokeWidth={1.2}>
        <line x1="8.5" y1="10" x2="8.5" y2="22" />
        <line x1="23.5" y1="10" x2="23.5" y2="22" />
      </g>
    </IconBase>
  );
});

/** ---------- OmniStudio (director frame + nodes) ---------- */
export const OmniStudioIcon: React.FC<IconProps> = memo(function OmniStudioIcon({
  size,
  title,
  className,
  strokeWidth = 1.9,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(185,95%,70%)" },
          { offset: "55%", color: "hsl(250,95%,72%)" },
          { offset: "100%", color: "hsl(312,95%,72%)" },
        ]}
      />
      <rect
        x="6"
        y="7"
        width="20"
        height="18"
        rx="3"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
      />
      <circle cx="11" cy="13" r="2.1" fill={`url(#${gradId})`} />
      <circle cx="21" cy="12" r="1.8" fill={`url(#${gradId})`} />
      <circle cx="17" cy="20" r="2" fill={`url(#${gradId})`} />
      <path
        d="M12.8 13.6 19 12.5M12.4 14.7l3.4 4M19.8 13.6l-2 4.2"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </IconBase>
  );
});

/** ---------- SynkStrive (trophy/bolt hybrid) ---------- */
export const SynkStriveIcon: React.FC<IconProps> = memo(function SynkStriveIcon({
  size,
  title,
  className,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient ?? { x1: "0%", y1: "0%", x2: "100%", y2: "0%" }}
        fallbackStops={[
          { offset: "0%", color: "hsl(150,95%,70%)" },
          { offset: "100%", color: "hsl(280,95%,70%)" },
        ]}
      />
      {/* Cup + base + bolt (filled with gradient) */}
      <path d="M10 8h12v3.8a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8z" fill={`url(#${gradId})`} />
      <path d="M12 22h8v2H12z" fill={`url(#${gradId})`} />
      <path d="M16 17l3 4h-6l3-4z" fill={`url(#${gradId})`} />
      {/* side handles (outlined glow) */}
      <path d="M10 10H7a3 3 0 0 0 3 3" fill="none" stroke={`url(#${gradId})`} strokeWidth={1.6} />
      <path d="M22 10h3a3 3 0 0 1-3 3" fill="none" stroke={`url(#${gradId})`} strokeWidth={1.6} />
    </IconBase>
  );
});

/** ---------- Coaches & AI Mentors (two heads + link) ---------- */
export const CoachesMentorsIcon: React.FC<IconProps> = memo(function CoachesMentorsIcon({
  size,
  title,
  className,
  strokeWidth = 2,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(210,95%,70%)" },
          { offset: "100%", color: "hsl(320,95%,70%)" },
        ]}
      />
      <circle cx="12" cy="13" r="4" fill={`url(#${gradId})`} />
      <circle cx="21.5" cy="11" r="3.3" fill={`url(#${gradId})`} />
      <path
        d="M6.5 22.5c1.4-2.7 4.1-4.5 7.5-4.5s6.1 1.8 7.5 4.5"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
      />
      {/* link */}
      <path
        d="M17 13c1.4 0 2.6 1.2 2.6 2.6M15 15.6c0-1.4 1.2-2.6 2.6-2.6"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1.6}
      />
    </IconBase>
  );
});

/** ---------- Smart Skins (mesh grid on panel) ---------- */
export const SmartSkinsIcon: React.FC<IconProps> = memo(function SmartSkinsIcon({
  size,
  title,
  className,
  strokeWidth = 2,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient ?? { x1: "0%", y1: "0%", x2: "100%", y2: "0%" }}
        fallbackStops={[
          { offset: "0%", color: "hsl(160,95%,70%)" },
          { offset: "100%", color: "hsl(260,95%,70%)" },
        ]}
      />
      <rect
        x="7" y="7" width="18" height="18" rx="3"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
      />
      <g fill="none" stroke={`url(#${gradId})`} strokeWidth={1.25}>
        <path d="M10 12h12M10 16h12M10 20h12" />
        <path d="M14 10v12M18 10v12" />
      </g>
    </IconBase>
  );
});

/** ---------- Smart Tattoos (stylus + waveform) ---------- */
export const SmartTattoosIcon: React.FC<IconProps> = memo(function SmartTattoosIcon({
  size,
  title,
  className,
  strokeWidth = 2,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient ?? { x1: "0%", y1: "100%", x2: "100%", y2: "0%" }}
        fallbackStops={[
          { offset: "0%", color: "hsl(200,95%,70%)" },
          { offset: "100%", color: "hsl(40,95%,70%)" },
        ]}
      />
      <path
        d="M6 24c3-5 7-8 12-10"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <path d="M18 12l6-6 2 2-6 6z" fill={`url(#${gradId})`} />
    </IconBase>
  );
});

/** ---------- NFTs / SKT (token hex + rails) ---------- */
export const NFTSKTIcon: React.FC<IconProps> = memo(function NFTSKTIcon({
  size,
  title,
  className,
  gradient,
}) {
  const gradId = safeId(useId());
  return (
    <IconBase size={size} title={title} className={className}>
      <HoloGradientDefs
        id={gradId}
        gradient={gradient}
        fallbackStops={[
          { offset: "0%", color: "hsl(280,95%,70%)" },
          { offset: "100%", color: "hsl(340,95%,70%)" },
        ]}
      />
      <polygon points="16,4 26,10 26,22 16,28 6,22 6,10" fill={`url(#${gradId})`} />
      <g fill="none" stroke={`url(#${gradId})`} strokeWidth={1.6}>
        <line x1="11" y1="16" x2="21" y2="16" />
        <line x1="13" y1="14" x2="13" y2="18" />
        <line x1="19" y1="14" x2="19" y2="18" />
      </g>
    </IconBase>
  );
});

/* ---------- Utility icons used by ServiceRail (new) ---------- */
export const MenuIcon: React.FC<IconProps> = memo(function MenuIcon({
  size = 18,
  title = "Menu",
  className,
  strokeWidth = 1.8,
}) {
  return (
    <IconBase size={size} title={title} className={className} viewBox="0 0 24 24">
      <g fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
      </g>
    </IconBase>
  );
});

export const GearIcon: React.FC<IconProps> = memo(function GearIcon({
  size = 18,
  title = "Settings",
  className,
  strokeWidth = 1.8,
}) {
  // Simple gear/sun glyph for settings
  return (
    <IconBase size={size} title={title} className={className} viewBox="0 0 24 24">
      <g fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 2v3" />
        <path d="M12 19v3" />
        <path d="M2 12h3" />
        <path d="M19 12h3" />
        <path d="M4.93 4.93l2.12 2.12" />
        <path d="M16.95 16.95l2.12 2.12" />
        <path d="M4.93 19.07l2.12-2.12" />
        <path d="M16.95 7.05l2.12-2.12" />
      </g>
    </IconBase>
  );
});

/* Optional: convenient map if you address by slug elsewhere */
export const SERVICE_ICON_MAP = {
  goalbuddy: GoalBuddyIcon,
  omnicast: OmniCastIcon,
  omnicine: OmniCineIcon,
  omnistudio: OmniStudioIcon,
  synkstrive: SynkStriveIcon,
  coaches: CoachesMentorsIcon,
  "smart-skins": SmartSkinsIcon,
  smartskins: SmartSkinsIcon,
  "smart-tattoos": SmartTattoosIcon,
  wallet: GoalBuddyIcon,
  nft: NFTSKTIcon, // ← matches your /nft route
};

